import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon, ChevronLeftIcon } from '@heroicons/react/24/solid';
import useClassNames from 'helpers/hooks/useClassNames';
import Image from 'frontastic/lib/image';
import DesktopImageMagnifier from './desktop-image-magnifier';
import MobileImageMagnifier from './mobile-image-magnifier';
import { ProductDetailsProps } from '../product/product-details';
import Magnifier from './magnifier';
import { Product, Variant } from 'shared/types/product';
import Typography from 'components/commercetools-ui/atoms/typography';
import sale from 'public/locales/en/product.json';
import { promotionCategory } from '../product/product-details/types';
import RightIcon from 'public/icons/ag-icons/chev-right.svg';
import LeftIcon from 'public/icons/ag-icons/chev-left.svg';
import { useCookies } from 'react-cookie';
import { ButtonBlock, CartDiscount, productSlug } from 'shared/types/product/Product';
import Link from 'components/commercetools-ui/atoms/link';
import { BreadCrumb } from 'shared/types/content/BreadCrumb';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import PlayButton from 'public/images/Stroke.svg';
import { useSwipeable } from 'react-swipeable';
import TrustBox from '../trustpilot';
import { TrustBoxCss } from 'frontastic/tastics/products/details';
import ChevronDown from 'public/icons/ag-icons/chevron-down-trustpilot.svg';

interface GalleryProps {
  images: Array<string>;
  productLogo?: string;
  inModalVersion?: ProductDetailsProps['inModalVersion'];
  product?: Product;
  variant?: Variant;
  hoveredImage?: string | null;
  breadCrumb?: BreadCrumb[];
  viewStandardFitOptions?: ButtonBlock[];
  allBadges?: any;
  cartDiscountBadge?: any;
  isDesktopSize?: boolean;
  isMobile?: boolean;
  selectedLocale?: string;
  handleScrollReviews?: any;
  trustBoxCss?: TrustBoxCss;
  trustPilotSkuId?: string;
  StarImageComponent?: any;
  starsAverage?: any;
  numberOfReviews?: any;
  discountPercentage?: number | null;
  mrrp?: number | null;
  variantPrice?: number | null;
}
const PDP_IMAGE_ZOOM_PROPS = {
  magnifierSize: 166,
};

const Gallery: FC<GalleryProps> = ({
  images,
  productLogo,
  inModalVersion,
  product,
  variant,
  hoveredImage,
  breadCrumb,
  viewStandardFitOptions,
  allBadges,
  isDesktopSize,
  isMobile,
  cartDiscountBadge,
  selectedLocale = 'en-GB',
  trustBoxCss,
  handleScrollReviews,
  trustPilotSkuId,
  StarImageComponent,
  starsAverage,
  numberOfReviews,
  discountPercentage,
  mrrp,
  variantPrice,
}) => {
  const [validImages, setValidImages] = useState<string[]>(images ?? []);
  const [activeSlide, setActiveSlide] = useState(0);
  const [startIndex, setStartIndex] = useState(0);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const displayedImage = hoveredImage || validImages[activeSlide];
  const promotionCategory = product?.categories as promotionCategory[] | undefined;
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  // const visibleItems = 6;
  const titleClassName = useClassNames(['break-normal mb-0', { 'md:text-24': !inModalVersion }]);

  const saleCategory = promotionCategory?.some((i: promotionCategory) => i?.slug == sale?.sale?.slug);
  const agExclusive = promotionCategory?.some((i: promotionCategory) => i?.slug == sale['AG-Exclusive'].slug);
  const newArrivals = promotionCategory?.find((i: promotionCategory) => i?.name === 'New Arrivals');
  const [cookies, setCookie] = useCookies(['locale_selection', 'frontastic-session']);
  const mrrpPounds = (mrrp ?? 0) / Math.pow(10, 2);
  const pricePounds = (variantPrice ?? 0) / Math.pow(10, 2);
  const [hasReviews, setHasReviews] = useState(false);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleRightClick(),
    onSwipedRight: () => handleLeftClick(),
    trackMouse: false,
    preventScrollOnSwipe: true,
  });

  // const isIOS = typeof window !== 'undefined' && /iPad|iPhone|iPod/?.test(navigator?.userAgent);

  const discountCategory = promotionCategory?.find(
    (category: promotionCategory) => category.name && /\d+%/.test(category.name),
  );

  const discountPercent = discountCategory?.name.match(/(\d+)%/)?.[1];

  const discountPromo = discountPercent ? `+Extra ${discountPercent}% Off At Checkout` : '';

  const discountUrl = discountCategory?.slug ?? '';
  // For CartDiscount
  const cartDiscount = useMemo(() => {
    try {
      return (
        (JSON.parse(
          (variant?.attributes?.cartDiscount ?? product?.variants?.[0]?.attributes?.cartDiscount) as string,
        ) as CartDiscount[]) || []
      );
    } catch {
      return [];
    }
  }, [variant]);

  // const discountPercentage =
  //   variant?.discountedPrice &&
  //   (((variant?.price?.centAmount as number) - (variant?.discountedPrice.centAmount as number)) /
  //     (variant?.price?.centAmount as number)) *
  //     100;

  const mrrpPoundsDiscount = useMemo(() => {
    if (!mrrpPounds || !pricePounds || mrrpPounds <= 0 || pricePounds <= 0) {
      return null;
    }
    return mrrpPounds - pricePounds;
  }, [mrrpPounds, pricePounds]);
  const handleLeftClick = () => {
    const newIndex = activeSlide === 0 ? validImages?.length - 1 : activeSlide - 1;
    setActiveSlide(newIndex);
    setSelectedSlide(newIndex);

    // if (newIndex < startIndex) {
    //   setStartIndex(Math.max(0, startIndex - 1));
    // }
    // Scroll thumbnail into view
    setTimeout(() => {
      thumbnailRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  };

  const handleRightClick = () => {
    const newIndex = activeSlide === validImages?.length - 1 ? 0 : activeSlide + 1;
    setActiveSlide(newIndex);
    setSelectedSlide(newIndex);

    // if (newIndex >= startIndex + visibleItems) {
    //   setStartIndex(Math.min(validImages?.length - visibleItems, startIndex + 1));
    // }
    setTimeout(() => {
      thumbnailRefs.current[newIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  };

  const handleThumbnailHover = (index: number) => {
    setActiveSlide(index);
  };

  const handleThumbnailClick = (index: number) => {
    setActiveSlide(index);
    setSelectedSlide(index);
  };

  const handleThumbnailMouseLeave = () => {
    setActiveSlide(selectedSlide);
  };
  const getRanking = (badge: any) => Number(badge?.ranking ?? badge?.ranking ?? 99);
  const sortedBadges = allBadges?.sort((a: any, b: any) => getRanking(a) - getRanking(b));
  const imagesContainerClassName = useClassNames([
    'relative lg:mx-auto',
    inModalVersion ? 'h-[400px] max-w-[340px]' : '',
  ]);
  const checkImageValidity = async (url: string): Promise<boolean> => {
    try {
      const response = await fetch(url, { method: 'GET' });
      return response?.ok;
    } catch (error) {
      console.error('Invalid image:', url);
      return false;
    }
  };

  useEffect(() => {
    const validateImages = async () => {
      const validationResults = await Promise.all(
        images?.map(async (imageUrl) => {
          const isValid = await checkImageValidity(imageUrl);
          return isValid ? imageUrl : null;
        }),
      );

      const filtered = validationResults?.filter((url): url is string => url !== null);
      setValidImages(filtered);
    };

    validateImages();
  }, [images]);

  if (validImages?.length === 0) {
    return (
      <div className={imagesContainerClassName}>
        <Image src={productLogo} suffix="large" style={{ objectFit: 'contain' }} fill />
      </div>
    );
  }
  const BrandLogo = `https://${product?.brandLogo?.BrandImage?.endpoint}.a.bigcontent.io/v1/static/${product?.brandLogo?.BrandImage?.name}`;
  return (
    <>
      <div className="block gap-y-34 px-4 md:mb-50 lg:hidden">
        <div className={`${imagesContainerClassName} relative`} {...swipeHandlers}>
          <div className="block sm:hidden">
            <div className="my-24 flex flex-col gap-y-24">
              {product?.brandLogo && (
                <div className="w-[100%] max-w-[150px]">
                  <img src={BrandLogo} className="w-[100%]" />
                </div>
              )}
              <Typography as="h1" className={`${titleClassName} text-start text-20 font-semibold leading-6`}>
                {product?.name}
              </Typography>
              {StarImageComponent && numberOfReviews !== 0 && (
                <div
                  onClick={() => handleScrollReviews()}
                  style={{
                    position: 'relative',
                  }}
                >
                  {/* <div className="mb-10 py-7" style={{ pointerEvents: 'none' }}>
                <TrustBox
                  id={trustPilotSkuId}
                  trustBoxCss={trustBoxCss}
                  showVertical={false}
                  onWidgetLoad={setHasReviews}
                />
              </div> */}
                  <div className="flex justify-start">
                    <div className="inline-flex cursor-pointer items-center gap-6 align-baseline">
                      {StarImageComponent && <StarImageComponent />}
                      {numberOfReviews !== 0 && (
                        <div className="flex items-center gap-8">
                          <span className="text-sm font-normal text-[#1E1E1E]">{numberOfReviews} reviews</span>
                          <ChevronDown />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {sortedBadges?.length > 0 && (
                <div className="flex gap-x-12">
                  {sortedBadges?.slice(0, 3)?.map((badge: any, idx: number) => {
                    const containerPosition =
                      (isDesktopSize ? badge?.visibility?.desktop?.position : badge?.visibility?.mobile?.position) ||
                      'center';
                    const styles = badge?.styles;
                    const isGradient = styles?.backgroundGradient?.startsWith('linear-gradient');
                    const iconPosition = badge?.icon?.position || 'left';
                    const iconType = badge?.icon?.positioning?.type || 'relative';
                    const shouldUseLineBreak = isDesktopSize
                      ? styles?.manualLineBreakDesktop
                      : styles?.manualLineBreakMobile;
                    const iconAsset = badge?.icon?.asset?.[selectedLocale];
                    const textStyles = {
                      fontSize: `${!isDesktopSize ? styles?.fontSizeMobilePx : styles?.fontSizeDesktopPx}px`,
                      fontWeight: styles?.fontWeight,
                      color: styles?.textColor,
                      letterSpacing: styles?.letterSpacing ? `${styles?.letterSpacing}px` : undefined,
                    };
                    const iconSize = isDesktopSize
                      ? Math.min(badge?.icon?.iconSizeDesktopPx ?? 0, 22)
                      : Math.min(badge?.icon?.iconSizeMobilePx ?? 0, 18);
                    const iconPositioning = badge?.icon?.positioning?.[isDesktopSize ? 'desktop' : 'mobile'] || {
                      top: 0,
                      left: 0,
                    };
                    const iconStyle: React.CSSProperties = {
                      position: iconType,
                      top: iconPositioning.top ?? 0,
                      left: iconPositioning.left ?? 0,
                      width: badge?.icon?.sizePx,
                      height: badge?.icon?.sizePx,
                      ...(iconPosition === 'left' && { marginRight: badge?.icon?.spacingPx ?? 0 }),
                      ...(iconPosition === 'right' && { marginLeft: badge?.icon?.spacingPx ?? 0 }),
                    };
                    const selfClass =
                      containerPosition === 'center'
                        ? 'self-center'
                        : containerPosition === 'right'
                        ? 'self-end'
                        : 'self-start';

                    const iconElement = iconAsset && (
                      <img
                        src={`https://${iconAsset.endpoint}.a.bigcontent.io/v1/static/${iconAsset.name}`}
                        alt=""
                        width={iconSize}
                        height={iconSize}
                        style={iconStyle}
                      />
                    );

                    const label = badge?.label?.[selectedLocale] || '';

                    return (
                      <span
                        key={badge?.id || badge?.badgeText || idx}
                        className={`inline-flex w-fit items-center ${selfClass}`}
                        style={{
                          ...(isGradient
                            ? { backgroundImage: styles?.backgroundGradient }
                            : { backgroundColor: styles?.backgroundColor }),
                          padding: !isDesktopSize ? styles?.paddingMobile : styles?.paddingDesktop,
                          borderRadius: styles?.borderRadius ? `${styles?.borderRadius}px` : undefined,
                          minHeight: `${!isDesktopSize ? styles?.minHeightMobilePx : styles?.minHeightDesktopPx}px`,
                          maxHeight: `${!isDesktopSize ? styles?.maxHeightMobilePx : styles?.maxHeightDesktopPx}px`,
                          gap: `${!isDesktopSize ? styles?.columnGapMobilePx : styles?.columnGapDesktopPx}px`,
                          borderWidth: `${styles?.borderThickness || 0}px`,
                          borderColor: styles?.borderColor,
                        }}
                      >
                        {iconPosition === 'left' && iconElement}
                        {shouldUseLineBreak ? (
                          <p className="text-center" style={textStyles} dangerouslySetInnerHTML={{ __html: label }} />
                        ) : (
                          <p className="text-center" style={textStyles}>
                            {label?.replace(/<[^>]*>/g, '')}
                          </p>
                        )}{' '}
                        {iconPosition === 'right' && iconElement}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div className="relative">
            <MobileImageMagnifier
              src={displayedImage || validImages?.[activeSlide] || validImages?.[0] || productLogo}
            />
            <div className="absolute left-0 top-0 z-10 flex flex-col gap-y-10">
              {product?.variants?.[0]?.attributes?.headcoverIncluded && (
                <div className="border-1 border-1 flex h-[23px] min-w-[50px] items-center justify-center rounded-md border border-[#057052] bg-white p-5">
                  <p
                    className="text-14 font-normal
                 text-[#057052]"
                  >
                    {'Headcover included'}
                  </p>
                </div>
              )}
              {typeof discountPercentage === 'number' &&
                discountPercentage > 0 &&
                typeof mrrpPoundsDiscount === 'number' &&
                mrrpPoundsDiscount && (
                  <div className="flex h-[26px] min-w-[51px] items-center justify-center gap-8 rounded-[2px] border border-[#686868] bg-white p-8">
                    <p
                      className={`text-center font-body text-11 font-bold not-italic leading-[110%] text-[#686868] sm:text-14`}
                    >
                      {mrrpPounds < 200
                        ? `-${discountPercentage}%`
                        : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                            mrrpPoundsDiscount,
                          )}`}
                    </p>
                  </div>
                )}
            </div>
            {!inModalVersion && validImages?.length > 1 && (
              <div className="absolute bottom-0 right-0 flex gap-8 p-16">
                <button
                  className={`border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === 0 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleLeftClick}
                  disabled={activeSlide === 0}
                >
                  <LeftIcon />
                </button>
                <button
                  className={`border-1 flex  h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === validImages?.length - 1 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleRightClick}
                  disabled={activeSlide === validImages?.length - 1}
                >
                  <RightIcon />
                </button>
              </div>
            )}
          </div>
          <div className="my-24 flex flex-col gap-y-12 sm:hidden">
            {/* As of commentted : Need to fix : DiscountText Promo */}
            {/* {product?.discountTextInfo?.discountText && (
          <div className="inline-flex cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
            {product?.discountTextInfo?.discountText}
          </div>
        )} */}
            {discountPromo && (
              <div className="px-4 py-10 lg:hidden">
                <a href={`/${discountUrl}`}>
                  <div className="mb-20 inline-flex w-full cursor-pointer items-center justify-center bg-green-100 p-2 px-4 py-2 text-16 font-medium leading-7">
                    {discountPromo}
                  </div>
                </a>
              </div>
            )}
            {cartDiscount?.length > 0 && (
              <div className="flex w-full flex-col gap-4 gap-y-12 text-center">
                {cartDiscount?.map((i: CartDiscount, index: number) => {
                  const desc = i?.description?.[cookies?.locale_selection?.currency === 'GBP' ? 'en-GB' : 'en-EU'];
                  if (desc) {
                    return (
                      <SmartLink
                        key={index}
                        href={`${breadCrumb?.[breadCrumb?.length - 1]?.href}shop-by/offer/${
                          i?.freeGiftName?.toLocaleLowerCase()
                            ? i?.freeGiftName?.toLocaleLowerCase()?.replace(/\s+/g, '_')?.replace('€', '€')
                            : desc?.replace(/\s+/g, '_')?.replace('€', '€')
                        }`}
                      >
                        <div
                          style={{
                            backgroundColor: cartDiscountBadge?.styles?.backgroundColor,
                            padding: !isDesktopSize
                              ? cartDiscountBadge?.styles?.paddingMobile
                              : cartDiscountBadge?.styles?.paddingDesktop,
                            borderRadius: cartDiscountBadge?.styles?.borderRadius,
                            minHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.minHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.minHeightDesktopPx}px`,
                            maxHeight: !isDesktopSize
                              ? `${cartDiscountBadge?.styles?.maxHeightMobilePx}px`
                              : `${cartDiscountBadge?.styles?.maxHeightDesktopPx}px`,
                          }}
                          className={`flex h-full min-h-[40px] w-full min-w-[86px] cursor-pointer flex-col items-center justify-center bg-ag_color-custom_green px-4 py-8 font-body text-14 font-medium capitalize text-black md:text-16 lg:text-18`}
                        >
                          {desc?.split(',')?.map((part, idx, arr) => (
                            <span
                              key={idx}
                              style={{
                                color: cartDiscountBadge?.styles?.textColor,
                                fontSize: !isDesktopSize
                                  ? cartDiscountBadge?.styles?.fontSizeMobilePx
                                  : cartDiscountBadge?.styles?.fontSizeDesktopPx,
                                fontWeight: cartDiscountBadge?.styles?.fontWeight,
                                letterSpacing: cartDiscountBadge?.styles?.letterSpacing,
                              }}
                            >
                              {part?.trim()}
                              {idx < arr?.length - 1 && <br />}
                            </span>
                          ))}
                        </div>
                      </SmartLink>
                    );
                  }
                  return null;
                })}
              </div>
            )}
            {Array.isArray(viewStandardFitOptions) &&
              Array.isArray(product?.popupData?.productSlug) &&
              product?.popupData?.productSlug?.some((item: productSlug) => item?.slug === product?.slug) && (
                <div className="View-Standard-options">
                  {viewStandardFitOptions?.map((optionBlock: any, idx: number) => {
                    const isCustomFit = product?.variants?.[0]?.attributes?.isCustomFit;

                    return Array.isArray(optionBlock?.buttons)
                      ? optionBlock?.buttons
                          ?.filter((btn: any) => btn?.isCustomFit === isCustomFit)
                          ?.map((btn: any, btnIdx: number) => {
                            const matchingSlug = product?.popupData?.productSlug?.find(
                              (item: productSlug) => item?.slug === product?.slug,
                            );

                            return (
                              <SmartLink
                                showLoader={false}
                                href={matchingSlug?.slugLink || ''}
                                key={`${idx}-${btnIdx}`}
                              >
                                <div className="relative w-fit">
                                  <button
                                    className="flex h-[32px] min-w-[216px] items-center justify-between rounded-[4px] pl-10 pr-10 font-body font-medium"
                                    style={{
                                      backgroundColor: btn?.buttonColour,
                                      color: btn?.TextColour,
                                      fontSize: btn?.TextFontSize,
                                    }}
                                  >
                                    <span className="w-full">{btn?.buttonText || 'View Standard Fit options'}</span>
                                    <span className="ml-10">
                                      <PlayButton />
                                    </span>
                                  </button>
                                </div>
                              </SmartLink>
                            );
                          })
                      : null;
                  })}
                </div>
              )}
          </div>

          {/* {product?.brandLogo && (
            <div className="absolute right-10 top-10 z-10 w-[100%] max-w-[80px]">
              <img src={BrandLogo} className="w-[100%]" />
            </div>
          )} */}
        </div>
      </div>
      <div className="hidden w-[100%] max-w-none lg:block ">
        <div className="align-center flex h-fit w-[100%] max-w-none justify-end gap-[1.5rem]">
          {!inModalVersion && (
            <div className="flex h-[100%] w-[92px] flex-col items-center">
              <div className="flex max-h-[640px] flex-col gap-16 overflow-scroll scrollbar-hide">
                {validImages?.map((img, index) => (
                  <div
                    key={startIndex + index}
                    ref={(el) => (thumbnailRefs.current[index] = el)}
                    className={`aspect-square relative h-[70px] w-[70px] rounded-md md:h-[85px] md:w-[85px] ${
                      startIndex + index === selectedSlide ? 'border border-neutral-500' : ''
                    }`}
                    style={{ aspectRatio: '1/1' }}
                    //   onMouseEnter={() => handleThumbnailHover(index)}
                    // onMouseLeave={handleThumbnailMouseLeave}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    <div className="relative h-full w-full">
                      <Image src={img || productLogo} suffix="small" className="rounded-md hover:cursor-pointer" fill />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="relative h-fit w-[100%]  max-w-none">
            {/* Need to Fix Discount Percentage */}
            {/* Zoom percentage display */}
            <div className="absolute left-10 top-10 z-10 flex flex-col gap-y-10">
              {product?.variants?.[0]?.attributes?.headcoverIncluded && (
                <div className="flex h-fit min-w-[57px] items-end justify-end rounded-md border border-[#42D6A1] bg-white p-[0.5rem]">
                  <p
                    className="font-body text-14 font-medium leading-[100%]
                 text-[#127E57]"
                  >
                    {'Headcover included'}
                  </p>
                </div>
              )}
              {typeof discountPercentage === 'number' &&
                discountPercentage > 0 &&
                typeof mrrpPoundsDiscount === 'number' &&
                mrrpPoundsDiscount && (
                  <div className="flex h-[26px] min-w-[51px] items-center justify-center gap-8 rounded-[2px] border border-[#686868] bg-white p-8">
                    <p
                      className={`text-center font-body text-11 font-bold not-italic leading-[110%] text-[#686868] sm:text-14`}
                    >
                      {mrrpPounds < 200
                        ? `-${discountPercentage}%`
                        : `-${cookies?.locale_selection?.currency === 'GBP' ? '£' : '€'}${Math.floor(
                            mrrpPoundsDiscount,
                          )}`}
                    </p>
                  </div>
                )}
            </div>
            <Magnifier src={displayedImage || validImages?.[activeSlide] || validImages?.[0] || productLogo} />
            {!inModalVersion && validImages?.length > 1 && (
              <div className="absolute bottom-0 right-0 flex gap-8 p-16">
                <button
                  className={` border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === 0 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleLeftClick}
                  disabled={activeSlide === 0}
                >
                  <LeftIcon />
                </button>
                <button
                  className={`border-1 flex h-32 w-32 items-center justify-center rounded-full border bg-white p-3 text-gray-700 ${
                    activeSlide === validImages?.length - 1 ? 'border-[#E7E7E7]' : 'border-black'
                  }`}
                  onClick={handleRightClick}
                  disabled={activeSlide === validImages?.length - 1}
                >
                  <RightIcon />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Gallery;
